import React, { useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { Text } from "@breakingwave/react-ui-components";
import TeamAccordion from "../../../../../../components/accordion/team-accordion";
import GroupStatusIcon from "../../../../../../components/group-status-icon";
import { IProjectTeamMemberDisplayItemsResponse } from "../../../../../../types/teamMemberDisplayItems";
import { AccordionToggleIconTypeEnum } from "../../../../../../types/accordions";
import { TaskGroupStatusesEnum, UserTypeEnum } from "../../../../../../types/shared";
import { teamWizardsStatusColors } from "../index.config";
import formatRateDisplay from "../../../../../../utils/formatRateDisplay";
import { ITaskGroupStatusResponse } from "../../../../../../types/groupStatuses";
import getUserType from "../../../../../../utils/getUserType";
import { getTeamName } from "../../../../../../utils/teams";
import * as S from "./index.styles";

interface ILeaveProjectWizardGroupStatusesProps {
    data: IProjectTeamMemberDisplayItemsResponse;
    defaultOpenState?: boolean;
}

/**
 * LeaveProjectGroupStatuses mode of <TeamWizards />.
 * @param {IProjectTeamMemberDisplayItemsResponse} data - Single team member data.
 * @param {boolean} [defaultOpenState] - An optional boolean prop which is passed down via <TeamWizards /> to render wizard accordion body open. Defaults to false.
 */
const LeaveProjectGroupStatusesWizard: React.FC<ILeaveProjectWizardGroupStatusesProps> = ({
    data: teamMember,
    ...rest
}) => {

    const [groupStatusPercentage, setgroupStatusPercentage] = React.useState<number>(0);

    useEffect(() => {
        if (teamMember) {
            const statusCompleteArr = teamMember.GroupStatuses.filter(
                (group) => group.Status === TaskGroupStatusesEnum.Complete
            );

            setgroupStatusPercentage((statusCompleteArr.length / teamMember.GroupStatuses.length) * 100);
        }
    }, [teamMember, ...teamMember.GroupStatuses.map((status) => status.Status)]);

    /**
     * This function runs every time "LeaveProjectGroupStatusesWizard.tsx" component mounts. Previously it was bound to useEffect with "data" dependency which was causing incorrect local member icon colours
     */
    const determineGroupFailedStatus = (group: ITaskGroupStatusResponse[]): boolean => {
        const statusFailedArr = group.filter((g) => g.Status === TaskGroupStatusesEnum.Failed);
        return !!statusFailedArr.length;
    };

    const {
        IsProjectAdmin,
        SubscriptionType
    } = teamMember;

    const userType: UserTypeEnum = useMemo(() => getUserType(IsProjectAdmin, SubscriptionType), [IsProjectAdmin, SubscriptionType]);

    return (
        <TeamAccordion
            profileProgressCircleProps={{
                size: 30,
                percentage: groupStatusPercentage,
                userType,
                percentagePathColour: determineGroupFailedStatus(teamMember.GroupStatuses)
                    ? teamWizardsStatusColors.LEAVE_PROJECT_GROUP_STATUSES_MODE.memberIcon.failed
                    : teamWizardsStatusColors.LEAVE_PROJECT_GROUP_STATUSES_MODE.memberIcon.default
            }}
            isToggleable={true}
            toggleIconType={AccordionToggleIconTypeEnum.Chevron}
            homeProjectName={teamMember.HomeProjectName}
            teamMemberDisplayName={`${teamMember.FirstName} ${teamMember.LastName}`}
            country={teamMember.Country}
            deviceType={teamMember.DeviceType}
            startDate={dayjs(teamMember.StartDate).format("DD MMM YYYY")}
            team={getTeamName(teamMember.Team)}
            rate={`${formatRateDisplay(teamMember.Rate, teamMember.RateCurrency)}pcm`}
            defaultOpenState={rest.defaultOpenState ?? false}
        >
            <S.GroupStatusesAccordionBody>
                <Text size={14} weight={500} style={{ marginBottom: 18 }}>
                    Current status
                </Text>
                <S.AllGroupStatusesWrapper>
                    {teamMember.GroupStatuses.map((group, i) => (
                        <S.EachGroupWrapper key={i}>
                            <S.GroupIcon>
                                <GroupStatusIcon
                                    size={26}
                                    status={group.Status}
                                    color={
                                        teamWizardsStatusColors.LEAVE_PROJECT_GROUP_STATUSES_MODE.status[group.Status]
                                    }
                                />
                            </S.GroupIcon>
                            <S.GroupName>{group.GroupId}</S.GroupName>
                            <S.GroupStatus
                                color={teamWizardsStatusColors.LEAVE_PROJECT_GROUP_STATUSES_MODE.status[group.Status]}
                            >
                                {group.Status}
                            </S.GroupStatus>
                        </S.EachGroupWrapper>
                    ))}
                </S.AllGroupStatusesWrapper>
            </S.GroupStatusesAccordionBody>
        </TeamAccordion>
    );
};

export default LeaveProjectGroupStatusesWizard;
